import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, User, Edit, Trash, Search, LayoutGrid, List } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UsuariosTab({ user, modulo = 'liga' }) {
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'turno',
    password: '',
    timezone: 'UTC'
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users?modulo=${modulo}`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingUser) {
        await axios.put(`${API}/auth/users/${editingUser.id}`, formData, { withCredentials: true });
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/auth/users`, { ...formData, modulo }, { withCredentials: true });
        toast.success('Usuario creado exitosamente');
      }
      setShowDialog(false);
      setEditingUser(null);
      setFormData({ email: '', name: '', role: 'turno', password: '', timezone: 'UTC' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({ email: u.email, name: u.name, role: u.role, password: '', timezone: u.timezone || 'UTC' });
    setShowDialog(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Estas seguro de eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/auth/users/${userId}`, { withCredentials: true });
      toast.success('Usuario eliminado');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const getRoleBadgeColor = (role) => {
    if (role === 'admin') return 'bg-purple-900/50 text-purple-300';
    if (role === 'super_user') return 'bg-blue-900/50 text-blue-300';
    return 'bg-slate-700 text-slate-300';
  };

  const getRoleLabel = (role) => {
    if (role === 'admin') return 'Administrador';
    if (role === 'super_user') return 'Super Usuario';
    return 'Turno';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-white">Usuarios</h2>
        <Button data-testid="create-user-btn" onClick={() => setShowDialog(true)}
          className="gap-2 bg-white text-slate-900 hover:bg-slate-200">
          <Plus className="w-4 h-4" /> Nuevo Usuario
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar usuario..." className="pl-9 bg-slate-700 border-slate-600 text-white"
            data-testid="usuarios-search" />
        </div>
        <div className="flex border border-slate-600 rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('cards')}
            className={`p-2 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
            data-testid="usuarios-view-cards"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
            data-testid="usuarios-view-list"><List className="w-4 h-4" /></button>
        </div>
      </div>

      {(() => {
        const filtered = users.filter(u =>
          !searchTerm || u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filtered.length === 0) {
          return <div className="text-center py-12 text-slate-500">{searchTerm ? 'Sin resultados' : 'No hay usuarios'}</div>;
        }

        if (viewMode === 'list') {
          return (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-700 text-slate-400 text-xs uppercase">
                  <th className="px-4 py-3 text-left">Usuario</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Rol</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr></thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                            {u.picture ? <img src={u.picture} alt="" className="w-full h-full rounded-full" /> : <User className="w-3.5 h-3.5 text-slate-400" />}
                          </div>
                          <span className="text-white font-medium text-sm">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{u.email}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs rounded ${getRoleBadgeColor(u.role)}`}>{getRoleLabel(u.role)}</span></td>
                      <td className="px-4 py-3 text-right">
                        {u.role !== 'admin' && (
                          <div className="flex gap-1 justify-end">
                            <Button onClick={() => handleEdit(u)} variant="outline" size="sm" className="h-7 px-2 border-slate-600 text-slate-300"><Edit className="w-3 h-3" /></Button>
                            <Button onClick={() => handleDelete(u.id)} variant="destructive" size="sm" className="h-7 px-2"><Trash className="w-3 h-3" /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((u) => (
          <Card key={u.id} className="p-4 hover:shadow-md transition-shadow bg-slate-800 border-slate-700" data-testid="user-card">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                {u.picture ? (
                  <img src={u.picture} alt={u.name} className="w-full h-full rounded-full" />
                ) : (
                  <User className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm truncate text-white">{u.name}</h3>
                <p className="text-xs text-slate-400 truncate">{u.email}</p>
                <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${getRoleBadgeColor(u.role)}`}>
                  {getRoleLabel(u.role)}
                </span>
              </div>
            </div>
            {u.role !== 'admin' && (
              <div className="flex gap-2">
                <Button
                  data-testid={`edit-user-${u.id}`}
                  onClick={() => handleEdit(u)}
                  variant="outline"
                  size="sm"
                  className="gap-2 flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </Button>
                <Button
                  data-testid={`delete-user-${u.id}`}
                  onClick={() => handleDelete(u.id)}
                  variant="destructive"
                  size="sm"
                  className="gap-2 flex-1"
                >
                  <Trash className="w-4 h-4" />
                  Eliminar
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
        );
      })()}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="user-email-input" type="email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} required
                className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Nombre</Label>
              <Input data-testid="user-name-input" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Rol</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="user-role-select" className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  <SelectItem value="super_user">Super Usuario</SelectItem>
                  <SelectItem value="turno">Turno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Contrasena {editingUser && '(dejar vacio para no cambiar)'}</Label>
              <Input data-testid="user-password-input" type="password" value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingUser}
                className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <Button data-testid="user-save-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
              {loading ? 'Guardando...' : (editingUser ? 'Actualizar Usuario' : 'Crear Usuario')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
